import React from "react";
import styled from "styled-components";
import { Card, FlexContainer } from "../../styles/GlobalStyles";

const StatsCardContainer = styled(Card)`
  position: relative;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`;

const StatsHeader = styled(FlexContainer)`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StatsTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatsValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize["2xl"]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const StatsSubtitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StatsFooter = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ColorBar = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "color",
})<{ color: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background-color: ${({ color }) => color};
`;

const IconContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !["bgColor", "iconColor"].includes(prop),
})<{ bgColor: string; iconColor: string }>`
  width: 48px;
  height: 48px;
  background-color: ${({ bgColor }) => bgColor};
  color: ${({ iconColor }) => iconColor};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface StatsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  footer?: string;
  icon?: React.ReactNode;
  color: string;
  bgColor?: string;
  iconColor?: string;
}

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  subtitle,
  footer,
  icon,
  color,
  bgColor,
  iconColor = "white",
}) => {
  const defaultBgColor = bgColor || `${color}15`;

  return (
    <StatsCardContainer>
      <ColorBar color={color} />

      <StatsHeader justify="space-between">
        <StatsTitle>{title}</StatsTitle>
        {icon && (
          <IconContainer bgColor={defaultBgColor} iconColor={iconColor}>
            {icon}
          </IconContainer>
        )}
      </StatsHeader>

      <StatsValue>{value}</StatsValue>

      {subtitle && <StatsSubtitle>{subtitle}</StatsSubtitle>}

      {footer && <StatsFooter>{footer}</StatsFooter>}
    </StatsCardContainer>
  );
};
